import { createContext, useCallback, useContext, useMemo, useState } from "react";

// Minimal bilingual context. Manual EN/FR toggle, persisted in localStorage.

const LangContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

const STRINGS = {
  // Age gate
  age_ribbon: { en: "Age verification", fr: "Vérification d'âge" },
  age_title_q: { en: "Are you", fr: "Avez-vous" },
  age_title_age: { en: "19+", fr: "19+" },
  age_title_end: { en: "?", fr: " ans ou plus ?" },
  age_body: {
    en: "You must be of legal age in Ontario/Québec to enter. By continuing you confirm you are 19 years or older.",
    fr: "Vous devez avoir l'âge légal en Ontario/Québec pour entrer. En continuant, vous confirmez avoir 19 ans ou plus.",
  },
  age_yes: { en: "I'm 19 or older", fr: "J'ai 19 ans ou plus" },
  age_no: { en: "I'm under 19", fr: "J'ai moins de 19 ans" },
  age_denied: { en: "Sorry — you must be 19 or older to enter this site.", fr: "Désolé — vous devez avoir 19 ans ou plus pour accéder à ce site." },

  // Hero
  open_now: { en: "Open now — 24/7", fr: "Ouvert — 24/7" },
  h1_a: { en: "Anytime.", fr: "Jour." },
  h1_b: { en: "Anything.", fr: "Nuit." },
  h1_c: { en: "Anywhere.", fr: "Partout." },
  hero_body: {
    en: "Ottawa & Gatineau's one-stop late-night delivery. Liquor, wine, vapes, shisha and everything in between — at your door, day or night.",
    fr: "La livraison nocturne tout-en-un d'Ottawa et Gatineau. Alcool, vin, vapes, shisha et tout le reste — à votre porte, jour et nuit.",
  },
  text_to_order: { en: "Text to Order", fr: "Texter pour commander" },
  view_menu: { en: "View the menu", fr: "Voir le menu" },
  live: { en: "live", fr: "live" },
  open_every_night: { en: "Open every night", fr: "Ouvert chaque nuit" },
  free_120: { en: "Free $120+", fr: "Gratuit 120 $+" },
  delivery_zone: { en: "Delivery in-zone", fr: "Livraison en zone" },
  text_us: { en: "Text us", fr: "Textez-nous" },

  // Promo ticker
  promo_1: { en: "Free delivery on orders over $120", fr: "Livraison gratuite dès 120 $" },
  promo_2: { en: "$100 bottles — text us for pricing", fr: "Bouteilles 100 $ — textez pour les prix" },
  promo_3: { en: "Everyday deals — ask the menu", fr: "Promos chaque jour — demandez le menu" },
  promo_4: { en: "24/7 — Ottawa & Gatineau", fr: "24/7 — Ottawa et Gatineau" },
  promo_5: { en: "Oz deals available — message for menu", fr: "Promos sur l'once — textez pour le menu" },

  // QR
  qr_ribbon: { en: "Scan · Menu · Order", fr: "Scanner · Menu · Commander" },
  qr_h1: { en: "Scan the bar code.", fr: "Scannez le code-barres." },
  qr_h2: { en: "Get the menu.", fr: "Obtenez le menu." },
  qr_body: {
    en: "Walk past one of our flyers or QR cards in Ottawa or Gatineau? Snap the code with your camera and you'll land right on the live menu — text to order in seconds.",
    fr: "Vous croisez un de nos flyers ou cartes QR à Ottawa ou Gatineau ? Scannez-le avec votre caméra et tombez directement sur le menu — textez pour commander en quelques secondes.",
  },

  // Daily deals
  deals_ribbon: { en: "Today's drops", fr: "Promos du jour" },
  deals_title: { en: "Tonight's deals.", fr: "Promos de la soirée." },
  deals_sub: { en: "Featured nightly — text us to lock it in.", fr: "Vedettes chaque nuit — textez pour réserver." },

  // Menu
  menu_ribbon: { en: "The menu", fr: "Le menu" },
  menu_title: { en: "Everything we deliver.", fr: "Tout ce qu'on livre." },
  menu_sub_a: { en: "Tap any category to open the list — every item has a text-to-order button.", fr: "Touchez une catégorie pour voir la liste — chaque article a un bouton texter-pour-commander." },
  menu_sub_b: { en: "18 categories, one number.", fr: "18 catégories, un seul numéro." },
  bottles_title: { en: "$100 bottles?", fr: "Bouteilles 100 $ ?" },
  bottles_body: { en: "Text us for our pricing & everyday deals — we keep the best moves off the public menu.", fr: "Textez-nous pour les prix et promos — on garde les meilleurs deals hors du menu public." },
  view_items: { en: "View items", fr: "Voir les articles" },

  // Sheet
  sheet_close: { en: "Close", fr: "Fermer" },
  sheet_cta: { en: "Text us about", fr: "Textez à propos de" },
  sheet_all: { en: "Text us — all options", fr: "Textez — toutes les options" },
  sheet_pricing_note: { en: "Tap an item to start a text — we'll reply with current pricing & stock.", fr: "Touchez un article pour démarrer un texto — on répond avec les prix et stock." },

  // Sticky / Footer
  call: { en: "Call", fr: "Appeler" },
  hours: { en: "Hours", fr: "Heures" },
  open_247: { en: "Open 24/7", fr: "Ouvert 24/7" },
  zone: { en: "Zone", fr: "Zone" },
  free_over: { en: "Free over", fr: "Gratuit dès" },
  phone_label: { en: "Phone", fr: "Téléphone" },
  must_19: { en: "Must be 19+. Please consume responsibly.", fr: "19 ans ou plus. Consommez avec modération." },
  rights_reserved: { en: "All rights reserved.", fr: "Tous droits réservés." },
  ottawa_gatineau_delivery: { en: "Ottawa & Gatineau delivery", fr: "Livraison Ottawa et Gatineau" },
  tagline_short: { en: "Anytime · Anything · Anywhere", fr: "Jour · Nuit · Partout" },
  footer_blurb: { en: "Late-night delivery for Ottawa & Gatineau. Liquor, wine, vapes, shisha & everything in between. Text us — we move fast.", fr: "Livraison nocturne à Ottawa et Gatineau. Alcool, vin, vapes, shisha et tout le reste. Textez — on bouge vite." },
};

const initialLang = () => {
  if (typeof window === "undefined") return "en";
  try {
    const params = new URLSearchParams(window.location.search);
    const ql = params.get("lang");
    if (ql === "fr" || ql === "en") {
      window.localStorage.setItem("oss-lang", ql);
      return ql;
    }
  } catch (e) {
    console.warn("LangContext: failed to parse ?lang query", e);
  }
  const saved = window.localStorage.getItem("oss-lang");
  return saved === "fr" || saved === "en" ? saved : "en";
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(initialLang);

  const update = useCallback((l) => {
    setLang(l);
    window.localStorage.setItem("oss-lang", l);
  }, []);

  const t = useCallback((key) => {
    const entry = STRINGS[key];
    if (!entry) return key;
    return entry[lang] || entry.en || key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang: update, t }), [lang, update, t]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
